/**
 * useApplicantApplicationState — the single authoritative applicant-state read
 * path (applicant-application-flow spec, task 3).
 *
 * Both the Portal Dashboard and the `/apply` Application Surface consume THIS
 * hook so they can never disagree about the applicant's Application State
 * (Req 10 / Correctness Property 5). It owns the two reads the decision needs —
 * the published Form_Version (`getActiveFormVersion`) and the applicant's own
 * `ApplicationResponse` (`getMyApplicationResponse`) — and feeds the shared,
 * submission-aware pure resolver `resolveApplicantAppState`.
 *
 * The window/season context (`intakeOpen`, `selectedDivisionKey`) is supplied by
 * the caller, which already derives it (the surface from
 * `useActiveIntakeSeasons` + the resolved `divisionKey`; the dashboard from the
 * active-intake set). The authoritative status source is the new
 * `ApplicationResponse.submissionStatus` — the legacy `ApplicantYearProfile`
 * status is intentionally NOT read here.
 *
 * The `reviewReturn` marker (director "request changes") now travels on the
 * `ApplicationResponse` DTO (application-decision-feedback); it is threaded into
 * the resolver so a `DRAFT` + `reviewReturn` response resolves to
 * `CHANGES_REQUESTED` rather than a plain `IN_PROGRESS` draft.
 *
 * Validates: Requirements 4, 6, 7, 10, 11.1, 11.2
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { applicationFormClient } from '@/lib/application-form/client';
import type { ApplicationResponse } from '@/lib/application-form/client';
import {
  resolveApplicantAppState,
  type ApplicantAppState,
  type ApplicationStatusValue,
} from '@/components/application-form/renderer/pure';

export interface UseApplicantApplicationStateArgs {
  /** The competition the applicant is applying to. */
  competitionId: string;
  /** Whether the target season's intake window is open now (caller-derived). */
  intakeOpen: boolean;
  /**
   * Whether a published Form_Version exists for the season/division
   * (caller-derived — the surface already loads it; the dashboard resolves it
   * from the active-season/form state). Kept an input so this hook owns exactly
   * one read (the applicant's response) and never double-loads the form.
   */
  hasPublishedForm: boolean;
  /** The applicant's selected division for the season, or null/'' when none. */
  selectedDivisionKey: string | null;
}

export interface UseApplicantApplicationStateResult {
  /** The resolved applicant-facing Application State. */
  state: ApplicantAppState;
  /** The loaded response (null until loaded / when none exists). */
  response: ApplicationResponse | null;
  loading: boolean;
  error: string | null;
  /** Re-run the reads (e.g. after a successful submit) to re-resolve the state. */
  refetch: () => void;
}

/**
 * Read the applicant's Application State for a competition. While loading the
 * state is `NOT_STARTED` as a neutral placeholder; callers should branch on
 * `loading`/`error` first (Req 11.1, 11.2).
 */
export function useApplicantApplicationState({
  competitionId,
  intakeOpen,
  hasPublishedForm,
  selectedDivisionKey,
}: UseApplicantApplicationStateArgs): UseApplicantApplicationStateResult {
  const [response, setResponse] = useState<ApplicationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refetch = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    applicationFormClient
      .getMyApplicationResponse(competitionId)
      .then((saved) => {
        if (cancelled || !mountedRef.current) return;
        setResponse(saved);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled || !mountedRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load your application.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [competitionId, attempt]);

  // The division that scopes the stage decision. Prefer the caller-supplied key
  // (a just-committed selection), but fall back to the division carried on the
  // loaded response — resolved server-side from Participation by
  // getMyApplicationResponse. This is the season-scoped SSOT, so a returning
  // contestant who already chose a division this season (e.g. one whose
  // application was returned for changes) resolves to IN_PROGRESS /
  // CHANGES_REQUESTED instead of being sent back to DIVISION_ENTRY. The caller's
  // legacy ApplicantYearProfile-derived prop is often empty post-cutover; the
  // response's Participation-backed divisionKey is authoritative.
  const effectiveDivisionKey =
    selectedDivisionKey != null && selectedDivisionKey.trim() !== ''
      ? selectedDivisionKey
      : response?.divisionKey ?? null;

  const state = resolveApplicantAppState({
    intakeOpen,
    hasPublishedForm,
    selectedDivisionKey: effectiveDivisionKey,
    applicationStatus: (response?.submissionStatus ?? null) as ApplicationStatusValue | null,
    // The director "request changes" marker now travels on the response DTO
    // (application-decision-feedback): a DRAFT + reviewReturn resolves to
    // CHANGES_REQUESTED rather than a plain IN_PROGRESS draft.
    reviewReturn: response?.reviewReturn ?? false,
  });

  return { state, response, loading, error, refetch };
}

export default useApplicantApplicationState;
